import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import NotificationService, { CHAVES_NOTIFICACOES } from '../api/NotificationService';
import { NotificationPrefs } from '../types/dtos';
import { getApiErrorMessage } from '../utils/http';
import { useToast } from '../context/ToastContext';

/**
 * Central de notificações (o sininho). O push é o aviso; isto é o registro — e é o que
 * quem não deu permissão, ou está no iPhone sem o app na tela de início, enxerga.
 */
export default function useNotifications() {
  const query = useQuery({
    queryKey: CHAVES_NOTIFICACOES.caixa,
    queryFn: async () => (await NotificationService.getInbox()).data,
    // O badge fica velho se ninguém refizer a busca: o push chega fora do React e a
    // notificação nasce no servidor. 5 min é bem mais barato que um websocket para
    // um app que se abre algumas vezes por dia.
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  return {
    itens: query.data?.items ?? [],
    naoLidas: query.data?.unread ?? 0,
    carregando: query.isLoading,
    // Direto do service, sem useMutation: ninguém lê estado de envio destes dois, e a
    // invalidação já viaja junto da chamada (`invalidando`, no NotificationService).
    marcarComoLida: NotificationService.marcarComoLida,
    marcarTodasComoLidas: NotificationService.marcarTodasComoLidas,
  };
}

/** Preferências do que receber e a que horas. Usado na tela de Configurações. */
export function useNotificationPrefs() {
  const cliente = useQueryClient();
  const toast = useToast();

  const query = useQuery({
    queryKey: CHAVES_NOTIFICACOES.preferencias,
    queryFn: async () => (await NotificationService.getPreferencias()).data,
  });

  const salvar = useMutation({
    mutationFn: (prefs: NotificationPrefs) => NotificationService.salvarPreferencias(prefs),
    // Escreve a resposta do servidor no cache em vez de invalidar — por isso o service
    // NÃO invalida nesta chamada: um refetch faria a UI piscar de volta para o valor
    // antigo entre o salvar e a resposta nova.
    onSuccess: (resposta) => {
      cliente.setQueryData(CHAVES_NOTIFICACOES.preferencias, resposta.data);
      toast.sucesso('Preferências salvas.');
    },
    onError: (e) => toast.erro(getApiErrorMessage(e, 'Não foi possível salvar as preferências.')),
  });

  return {
    prefs: query.data,
    carregando: query.isLoading,
    salvar: salvar.mutate,
    salvando: salvar.isPending,
  };
}
